/* eslint-disable max-lines */
import PropTypes from 'prop-types';
import React, { Fragment, Suspense } from 'react';

import CartOverlay from 'Component/CartOverlay';
import { CART_OVERLAY_POPUP_ID } from 'Component/CartOverlay/CartOverlay.config';
import CartPopup from 'Component/CartPopup';
import ChevronIcon from 'Component/ChevronIcon';
import { BOTTOM, LEFT, TOP } from 'Component/ChevronIcon/ChevronIcon.config';
import ClickOutside from 'Component/ClickOutside';
import CurrencySwitcher from 'Component/CurrencySwitcher';
import CustomerService from 'Component/CustomerService';
import {
    CART, CART_EDITING,
    CART_OVERLAY,
    CATEGORY, CHECKOUT, CHECKOUT_ACCOUNT, CHECKOUT_SUCCESS, CMS_PAGE, CONTACT_US,
    CUSTOMER_ACCOUNT, CUSTOMER_ACCOUNT_PAGE, CUSTOMER_ORDER, CUSTOMER_SUB_ACCOUNT,
    CUSTOMER_WISHLIST, FILTER,
    MENU, MENU_SUBCATEGORY,
    NO_MATCH,
    PDP,
    POPUP, PRODUCT_COMPARE, SEARCH
} from 'Component/Header/Header.config';
import HeartIcon from 'Component/HeartIcon';
import Link from 'Component/Link';
import Logo from 'Component/Logo';
import Menu from 'Component/Menu';
import { DEFAULT_STATE_NAME } from 'Component/NavigationAbstract/NavigationAbstract.config';
import OfflineNotice from 'Component/OfflineNotice';
import PaymentMethods from 'Component/PaymentMethods';
import Popup from 'Component/Popup';
import SearchField from 'Component/SearchField';
import SearchIcon from 'Component/SearchIcon';
import SearchPopup from 'Component/SearchPopup';
import StoreLocatorLink from 'Component/StoreLocatorLink';
import StoreSwitcher from 'Component/StoreSwitcher';
import SwitcherIcon from 'Component/SwitcherIcon';
import UserIcon from 'Component/UserIcon';
import {
    ACCOUNT_PRO_LOGIN_URL,
    ACCOUNT_STUDENT_LOGIN_URL,
    GROUP_ID_EMPLOYEE,
    GROUP_ID_PRO, GROUP_ID_STUDENT
} from 'Route/MyAccount/MyAccount.config';
import {
    CartOverlay as SourceCartOverlay,
    Header as SourceHeader,
    MyAccountOverlay
} from 'SourceComponent/Header/Header.component';
import logoMobile from 'Style/images/logo-mobile.svg';
import { ItemType } from 'Type/ProductList.type';
import { isCrawler, isSSR } from 'Util/Browser';
import CSS from 'Util/CSS';
import media from 'Util/Media';
import { LOGO_MEDIA } from 'Util/Media/Media';

import './Header.override.style';

export {
    SourceCartOverlay as CartOverlay,
    MyAccountOverlay
};

/** @namespace PeggysagePwa/Component/Header/Component */
export class HeaderComponent extends SourceHeader {
    static propTypes = {
        ...SourceHeader.propTypes,
        onMenuButtonClick: PropTypes.func.isRequired,
        onMenuClose: PropTypes.func.isRequired,
        isMenuMobileOpened: PropTypes.bool.isRequired,
        onWishlistButtonClick: PropTypes.func.isRequired,
        isWishlistEnabled: PropTypes.bool.isRequired,
        isCustomerTypeStudentEnabled: PropTypes.bool.isRequired,
        wishlistItems: PropTypes.objectOf(ItemType).isRequired,
        onSwitcherButtonClick: PropTypes.func.isRequired,
        onSwitcherOutsideClick: PropTypes.func.isRequired,
        onLogoClick: PropTypes.func.isRequired,
        isSwitcherOpened: PropTypes.bool.isRequired,
        userGroupId: PropTypes.number.isRequired,
        isHomePage: PropTypes.bool
    };

    static defaultProps = {
        ...SourceHeader.defaultProps,
        isHomePage: false
    };

    stateMap = {
        [DEFAULT_STATE_NAME]: {
            title: true,
            logo: true
        },
        [NO_MATCH]: {
            title: true
        },
        [POPUP]: {
            title: true,
            close: true
        },
        [PDP]: {
            back: true,
            title: true
        },
        [CATEGORY]: {
            back: true,
            title: true
        },
        [CUSTOMER_ACCOUNT]: {
            title: true
        },
        [CUSTOMER_SUB_ACCOUNT]: {
            title: true,
            back: true
        },
        [CUSTOMER_ACCOUNT_PAGE]: {
            title: true
        },
        [CUSTOMER_WISHLIST]: {
            share: true,
            title: true
        },
        [CUSTOMER_ORDER]: {
            title: true,
            back: true
        },
        [MENU]: {
            search: false // true
        },
        [MENU_SUBCATEGORY]: {
            back: true,
            title: true,
            search: false // true
        },
        [SEARCH]: {
            search: false // true
        },
        [CART]: {
            title: true
        },
        [CART_OVERLAY]: {
            title: true
        },
        [CART_EDITING]: {
            ok: true,
            title: true,
            cancel: true
        },
        [FILTER]: {
            close: true,
            title: true
        },
        [CHECKOUT]: {
            back: true,
            title: true,
            account: true
        },
        [CHECKOUT_SUCCESS]: {
            title: true,
            account: true
        },
        [CHECKOUT_ACCOUNT]: {
            title: true,
            close: true
        },
        [CMS_PAGE]: {
            back: true,
            title: true
        },
        [CONTACT_US]: {
            title: true,
            back: true
        },
        [PRODUCT_COMPARE]: {
            title: true,
            back: true
        }
    };

    renderMap = {
        cancel: this.renderCancelButton.bind(this),
        back: this.renderBackButton.bind(this),
        close: this.renderCloseButton.bind(this),
        menu: this.renderMenuButton.bind(this), // NEW
        // title: this.renderTitle.bind(this),
        logo: this.renderLogoMobile.bind(this),
        search: this.renderSearchField.bind(this),
        storelocator: this.renderStoreLocatorButton.bind(this),
        wishlist: this.renderWishListButton.bind(this),
        account: this.renderAccount.bind(this),
        // compare: this.renderComparePageButton.bind(this),
        minicart: this.renderMinicart.bind(this),
        share: this.renderShareWishListButton.bind(this),
        ok: this.renderOkButton.bind(this)
    };

    /**
     * Override: disable cancel button in header
     * @returns {null}
     */
    renderCancelButton() {
        return null;
    }

    renderBackButtonLabel() {
        const {
            device: { isMobile },
            isCheckout
        } = this.props;

        if (!isCheckout) {
            return null;
        }

        const label = isMobile ? __('Panier') : __('Revenir à mon panier');
        return (
            <span>
                { label }
            </span>
        );
    }

    /**
     * Override: visible only in checkout
     * @param isVisible
     * @returns {JSX.Element|null}
     */
    renderBackButton(isVisible = false) {
        const {
            onBackButtonClick,
            // device: { isMobile },
            isCheckout
        } = this.props;

        if (!isCheckout) {
            return null;
        }

        return (
            <button
              key="back"
              block="Header"
              elem="Button"
              mods={ { type: 'back', isVisible } }
              onClick={ onBackButtonClick }
              aria-label="Go back"
              aria-hidden={ !isVisible }
              tabIndex={ isVisible ? 0 : -1 }
            >
                <ChevronIcon direction={ LEFT } />
                { this.renderBackButtonLabel() }
            </button>
        );
    }

    /**
     * Override: disable close button in header
     * @returns {null}
     */
    renderCloseButton() {
        return null;
    }

    renderMenuButton() {
        const {
            isCheckout,
            device: { isMobile },
            onMenuButtonClick,
            isMenuMobileOpened
        } = this.props;

        if (!isMobile || isCheckout) {
            return null;
        }

        return (
            <button
              type="button"
              block="Button"
              mods={ { active: isMenuMobileOpened } }
              mix={ { block: 'Header', elem: 'MenuButton' } }
              onClick={ onMenuButtonClick }
            >
                <span aria-hidden="true">{ __('Menu') }</span>
            </button>
        );
    }

    renderStoreLocatorButton() {
        const {
            isCheckout,
            device: { isMobile }
        } = this.props;

        if (isMobile || isCheckout) {
            return null;
        }

        return (
            <StoreLocatorLink
              hasLabel={ false }
              key="storelocator"
            />
        );
    }

    renderWishlistItemsQty() {
        const { wishlistItems } = this.props;

        const { length } = Object.keys(wishlistItems);

        if (!length) {
            return null;
        }

        return (
            <span block="Header" elem="WishlistItemCount">{ length }</span>
        );
    }

    renderWishListButton() {
        const {
            isCheckout,
            isWishlistEnabled,
            isWishlistLoading,
            onWishlistButtonClick
        } = this.props;

        if (isCheckout || !isWishlistEnabled) {
            return null;
        }

        return (
            <button
              key="wishlist"
              block="Header"
              elem="Button"
              mods={ { type: 'wishlist' } }
              onClick={ onWishlistButtonClick }
              aria-label="Open my wishlist"
              id="myWishlist"
              disabled={ isWishlistLoading }
            >
                <HeartIcon isActive={ false } />
                { this.renderWishlistItemsQty() }
            </button>
        );
    }

    /**
     * Override: Set different logo on mobile
     * @returns {JSX.Element}
     */
    renderLogoImage() {
        const {
            header_logo_src,
            logo_alt,
            logo_height,
            logo_width,
            device: { isMobile }
        } = this.props;

        // eslint-disable-next-line
        let logoSrc = logoMobile;

        if (!isMobile) {
            logoSrc = header_logo_src ? media(header_logo_src, LOGO_MEDIA) : null;

            CSS.setVariable(this.logoRef, 'header-logo-height', `${logo_height}px`);
            CSS.setVariable(this.logoRef, 'header-logo-width', `${logo_width}px`);
        } else {
            CSS.setVariable(this.logoRef, 'header-logo-height', '14px');
            CSS.setVariable(this.logoRef, 'header-logo-width', '145px');
        }

        return (
            <Logo
              src={ logoSrc }
              alt={ logo_alt }
              title={ logo_alt }
            />
        );
    }

    renderLogoMobile(isVisible = true) {
        const { device: { isMobile } } = this.props;

        if (!isMobile) {
            return null;
        }

        return this.renderLogo(isVisible);
    }

    renderLogoDesktop() {
        const { device: { isMobile } } = this.props;

        if (isMobile) {
            return null;
        }

        return this.renderLogo(true);
    }

    /**
     * Override: Change aria-label, visible by default, disable link in checkout
     * @param isVisible
     * @returns {JSX.Element|null}
     */
    renderLogo(isVisible = true) {
        const {
            isLoading,
            isCheckout,
            onLogoClick
        } = this.props;

        if (isLoading) {
            return null;
        }

        if (isCheckout) {
            return (
                <div
                  block="Header"
                  elem="LogoWrapper"
                  mods={ { isVisible } }
                  key="logo"
                >
                    { this.renderLogoImage() }
                </div>
            );
        }

        return (
            <Link
              to="/"
              aria-label="Go to homepage"
              aria-hidden={ !isVisible }
              tabIndex={ isVisible ? 0 : -1 }
              onClick={ onLogoClick }
              block="Header"
              elem="LogoWrapper"
              mods={ { isVisible } }
              key="logo"
            >
                { this.renderLogoImage() }
            </Link>
        );
    }

    /**
     * Override: Hide account overlay on mobile
     * @returns {JSX.Element|null}
     */
    renderAccountOverlay() {
        const {
            isCheckout,
            showMyAccountLogin,
            onSignIn,
            device: { isMobile }
        } = this.props;

        // This is here to prevent the popup-suspense from rendering
        if (!showMyAccountLogin) {
            return null;
        }

        if (isMobile) {
            return null;
        }

        return (
            <Suspense fallback={ this.renderAccountOverlayFallback() }>
                <MyAccountOverlay
                  onSignIn={ onSignIn }
                  isCheckout={ isCheckout }
                />
            </Suspense>
        );
    }

    /**
     * Override: Show account button on mobile too
     * @returns {JSX.Element}
     */
    renderAccountButton() {
        const {
            onMyAccountButtonClick,
            userGroupId
        } = this.props;

        return (
            <button
              block="Header"
              elem="MyAccountWrapper"
              tabIndex="0"
              onClick={ onMyAccountButtonClick }
              aria-label="Open my account"
              id="myAccount"
            >
                <UserIcon
                  isPro={ userGroupId === GROUP_ID_PRO || userGroupId === GROUP_ID_EMPLOYEE }
                  isStudent={ userGroupId === GROUP_ID_STUDENT }
                />
            </button>
        );
    }

    /**
     * Override: Show account button on mobile too, remove overlay
     * @param isVisible
     * @returns {JSX.Element}
     */
    renderAccount(isVisible = false) {
        const { isCheckout } = this.props;

        if (isCheckout) {
            return null;
        }

        return (
            <div key="account" block="Header" elem="MyAccountContainer">
                <div aria-label="My account" block="Header" elem="MyAccount">
                    { this.renderAccountButton(isVisible) }
                </div>
            </div>
        );
    }

    /**
     * Override: Hide minicart overlay on mobile + popup
     * @returns {JSX.Element|null}
     */
    renderMinicartOverlay() {
        const {
            device: { isMobile }
        } = this.props;

        if (isMobile) {
            return null;
        }

        return (
            <Popup
              id={ CART_OVERLAY_POPUP_ID }
              mix={ { block: 'CartOverlayPopup' } }
            >
                <CartOverlay />
            </Popup>
        );
    }

    /**
     * Override: Show minicart button also on mobile + add CartPopup
     * @param isVisible
     * @returns {JSX.Element|null}
     */
    renderMinicart(isVisible = false) {
        const { isCheckout } = this.props;

        if (isCheckout) {
            return null;
        }

        return (
            <Fragment key="minicart">
                <div
                  block="Header"
                  elem="Button"
                  mods={ { isVisible, type: 'minicart' } }
                >
                    { this.renderMinicartButton() }
                    { this.renderMinicartOverlay() }
                </div>
                <CartPopup />
            </Fragment>
        );
    }

    renderSwitcherButton() {
        const {
            onSwitcherButtonClick,
            isSwitcherOpened
        } = this.props;

        return (
            <button
              block="Header"
              elem="SwitcherButton"
              onClick={ onSwitcherButtonClick }
            >
                <SwitcherIcon />
                <ChevronIcon direction={ isSwitcherOpened ? TOP : BOTTOM } />
            </button>
        );
    }

    renderSwitcher() {
        const {
            onSwitcherOutsideClick,
            isSwitcherOpened
        } = this.props;

        return (
            <ClickOutside
              onClick={ onSwitcherOutsideClick }
              key="switcher"
            >
                <div block="Header" elem="Switcher">
                    { this.renderSwitcherButton() }
                    <div
                      block="Header"
                      elem="SwitcherContent"
                      mods={ { isVisible: isSwitcherOpened } }
                    >
                        <CurrencySwitcher />
                        <StoreSwitcher />
                    </div>
                </div>
            </ClickOutside>
        );
    }

    renderProPageLink() {
        return (
            <div block="Header" elem="ProButtonWrapper">
                <Link
                  to={ ACCOUNT_PRO_LOGIN_URL }
                  key="pro"
                  block="Header"
                  elem="ProButton"
                >
                    { __('Pro area') }
                </Link>
            </div>
        );
    }

    renderStudentPageLink() {
        const { userGroupId, isCustomerTypeStudentEnabled } = this.props;

        if (userGroupId === GROUP_ID_PRO || userGroupId === GROUP_ID_EMPLOYEE || !isCustomerTypeStudentEnabled) {
            return null;
        }

        return (
            <div block="Header" elem="StudentButtonWrapper">
                <Link
                  to={ ACCOUNT_STUDENT_LOGIN_URL }
                  key="student"
                  block="Header"
                  elem="StudentButton"
                >
                    { __('Student area') }
                </Link>
            </div>
        );
    }

    /**
     * Override: Remove news parts
     * @returns {JSX.Element|null}
     */
    renderTopMenu() {
        const {
            isCheckout,
            device: { isMobile }
        } = this.props;

        if (isMobile || isCheckout) {
            return null;
        }

        return (
            <div block="Header" elem="TopMenu" role="navigation" aria-label={ __('Secondary') }>
                { this.renderSwitcher() }
                { this.renderProPageLink() }
                { this.renderStudentPageLink() }
            </div>
        );
    }

    /**
     * Override: Show on mobile, passing new paramaters to Menu component
     * @returns {JSX.Element|null}
     */
    renderMenu() {
        const {
            isCheckout,
            isMenuMobileOpened,
            onMenuClose
        } = this.props;

        if (isCheckout) {
            return null;
        }

        return <Menu mods={ { isVisible: isMenuMobileOpened } } onClose={ onMenuClose } />;
    }

    /**
     * Override: disable ClickOutside on desktop (bug with popup opening)
     * @param isVisible
     * @returns {JSX.Element|null}
     */
    renderSearchField(isVisible = false) {
        const {
            searchCriteria,
            onSearchOutsideClick,
            onSearchBarFocus,
            onSearchBarChange,
            onClearSearchButtonClick,
            // navigationState: { name },
            isCheckout,
            hideActiveOverlay,
            device: { isMobile }
        } = this.props;

        if (isCheckout) {
            return null;
        }

        if (!isMobile) {
            const label = searchCriteria || __('Search products');

            return (
                <div block="SearchField" key="search">
                    <button
                      key="searchButton"
                      type="button"
                      block="Header"
                      elem="SearchButton"
                      onClick={ onSearchBarFocus }
                    >
                        <SearchIcon />
                        <span>{ label }</span>
                    </button>
                </div>
            );
        }

        return (
            <SearchField
              key="search"
              searchCriteria={ searchCriteria }
              onSearchOutsideClick={ onSearchOutsideClick }
              onSearchBarFocus={ onSearchBarFocus }
              onSearchBarChange={ onSearchBarChange }
              onClearSearchButtonClick={ onClearSearchButtonClick }
              isVisible={ isVisible }
              // isActive={ name === SEARCH }
              isActive={ false }
              hideActiveOverlay={ hideActiveOverlay }
              isClickOutsideDisabled
            />
        );
    }

    renderSearchMobile() {
        const { device: { isMobile } } = this.props;

        if (!isMobile) {
            return null;
        }

        return this.renderSearchField(true);
    }

    renderCheckout() {
        const {
            isCheckout,
            device: { isMobile }
        } = this.props;

        if (!isCheckout || isMobile) {
            return null;
        }

        return (
            <div
              block="Header"
              elem="Checkout"
            >
                <CustomerService hasContactLink={ false } />
                <PaymentMethods
                  hasTitle={ false }
                />
            </div>
        );
    }

    /**
     * Override: Add search field below nav on mobile
     * @returns {JSX.Element}
     */
    render() {
        const { stateMap } = this;
        const {
            navigationState: { name, isHiddenOnMobile = false },
            isCheckout,
            isHomePage,
            device: { isMobile }
        } = this.props;

        if (!isMobile) {
            // hide edit button on desktop
            stateMap[CUSTOMER_WISHLIST].edit = false;
            stateMap[CUSTOMER_WISHLIST].share = false;
            stateMap[CART_OVERLAY].edit = false;
        }

        return (
            <section
              block="Header"
              elem="Wrapper"
              mods={ { isPrerendered: isSSR() || isCrawler(), isCheckout, isHomePage } }
            >
                <header
                  block="Header"
                  mods={ { name, isHiddenOnMobile, isCheckout } }
                  mix={ { block: 'FixedElement', elem: 'Top' } }
                  ref={ this.logoRef }
                >
                    <div
                      block="Header"
                      elem="Content"
                    >
                        { this.renderTopMenu() }
                        { this.renderLogoDesktop() }
                        <nav block="Header" elem="Nav">
                            { this.renderNavigationState() }
                        </nav>
                        { this.renderCheckout() }
                    </div>
                    { this.renderSearchMobile() }
                    { this.renderMenu() }
                </header>
                <SearchPopup />
                <OfflineNotice />
            </section>
        );
    }
}

export default HeaderComponent;
